import { DOCUMENT } from "@angular/common";
import { Inject, Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { StorageService } from "src/@core/services/storage.service";
import { BUILD_RELEASE_TOKEN } from "../../../environments/build-release.token";

type BuildVersionSource = "startup" | "http" | "storage";

interface ReloadState {
  signature: string;
  count: number;
}

@Injectable({ providedIn: "root" })
export class BuildVersionService {
  private readonly buildVersionEnforced = environment.production;
  private readonly latestVersionStorageKey = "forlab_latest_build_version";
  private readonly authenticatedVersionStorageKey =
    "forlab_authenticated_build_version";
  private readonly reloadStateStorageKey = "forlab_build_reload_state";
  private readonly reloadGuardStorageKey = "forlab_build_reload_guard";
  private readonly remoteBuildVersionUrl = "/assets/build-release.json";
  private readonly remoteCheckIntervalMs = 15_000;
  private readonly hardReloadMaxAttempts = 2;
  private initialized = false;
  private currentBuildVersion = "unknown";
  private refreshInProgress = false;
  private remoteCheckInProgress = false;
  private lastRemoteCheckAt = 0;
  private periodicCheckTimer: ReturnType<typeof setInterval> | null = null;
  private readonly storageListener = (event: StorageEvent): void => {
    if (event.key === this.latestVersionStorageKey) {
      this.checkAndReloadIfVersionChanged("storage");
    }
  };

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly storageService: StorageService
  ) {}

  isEnabled(): boolean {
    return this.buildVersionEnforced;
  }

  initialize(): void {
    if (!this.buildVersionEnforced) {
      return;
    }
    if (this.initialized) {
      return;
    }
    this.initialized = true;
    this.currentBuildVersion = this.resolveCurrentBuildVersion();
    this.seedLatestVersion();
    this.seedAuthenticatedVersion();
    this.startPeriodicRemoteChecks();
    window.addEventListener("storage", this.storageListener);
    this.maybeCheckRemoteVersion("startup");
  }

  markAuthenticatedBuildVersion(): void {
    if (!this.buildVersionEnforced) {
      return;
    }
    if (!this.initialized) {
      this.initialize();
    }
    localStorage.setItem(
      this.authenticatedVersionStorageKey,
      this.currentBuildVersion
    );
    this.promoteLatestVersion(this.currentBuildVersion);
    this.resetReloadState();
  }

  checkAndReloadIfVersionChanged(source: BuildVersionSource): void {
    if (!this.buildVersionEnforced) {
      return;
    }
    if (!this.initialized) {
      this.initialize();
    }

    const latestKnownVersion = this.normalizeVersion(
      localStorage.getItem(this.latestVersionStorageKey)
    );
    if (!latestKnownVersion) {
      this.seedLatestVersion();
      return;
    }

    const compareResult = this.compareVersions(
      this.currentBuildVersion,
      latestKnownVersion
    );
    if (compareResult >= 0) {
      if (compareResult > 0) {
        // Current tab is newer than storage value: promote this version.
        this.promoteLatestVersion(this.currentBuildVersion);
      }
      this.resetReloadState();
      this.maybeCheckRemoteVersion(source);
      return;
    }

    this.handleVersionMismatch(latestKnownVersion, source);
  }

  private seedLatestVersion(): void {
    const latest = this.normalizeVersion(
      localStorage.getItem(this.latestVersionStorageKey)
    );
    if (!latest) {
      localStorage.setItem(this.latestVersionStorageKey, this.currentBuildVersion);
      return;
    }
    if (this.compareVersions(this.currentBuildVersion, latest) > 0) {
      localStorage.setItem(this.latestVersionStorageKey, this.currentBuildVersion);
    }
  }

  private seedAuthenticatedVersion(): void {
    const authenticated = this.normalizeVersion(
      localStorage.getItem(this.authenticatedVersionStorageKey)
    );
    if (authenticated) {
      return;
    }
    if (!this.isAuthenticatedUserPresent()) {
      return;
    }
    localStorage.setItem(
      this.authenticatedVersionStorageKey,
      this.currentBuildVersion
    );
  }

  private startPeriodicRemoteChecks(): void {
    if (this.periodicCheckTimer) {
      return;
    }
    this.periodicCheckTimer = setInterval(() => {
      this.maybeCheckRemoteVersion("http");
    }, 60_000);
  }

  private maybeCheckRemoteVersion(source: BuildVersionSource): void {
    const now = Date.now();
    if (this.remoteCheckInProgress) {
      return;
    }
    if (
      source === "http" &&
      this.lastRemoteCheckAt > 0 &&
      now - this.lastRemoteCheckAt < this.remoteCheckIntervalMs
    ) {
      return;
    }

    this.remoteCheckInProgress = true;
    this.lastRemoteCheckAt = now;
    this.fetchRemoteBuildVersion()
      .then((remoteVersion) => {
        if (!remoteVersion) {
          return;
        }
        const latestKnownVersion = this.normalizeVersion(
          localStorage.getItem(this.latestVersionStorageKey)
        );
        if (
          !latestKnownVersion ||
          this.compareVersions(remoteVersion, latestKnownVersion) > 0
        ) {
          localStorage.setItem(this.latestVersionStorageKey, remoteVersion);
          this.checkAndReloadIfVersionChanged("storage");
          return;
        }
        if (this.compareVersions(this.currentBuildVersion, remoteVersion) > 0) {
          this.promoteLatestVersion(this.currentBuildVersion);
        }
      })
      .finally(() => {
        this.remoteCheckInProgress = false;
      });
  }

  private async fetchRemoteBuildVersion(): Promise<string | null> {
    if (typeof window === "undefined" || typeof fetch !== "function") {
      return null;
    }
    try {
      const response = await fetch(
        `${this.remoteBuildVersionUrl}?v=${Date.now()}`,
        {
          cache: "no-store",
          credentials: "same-origin",
        }
      );
      if (!response.ok) {
        return null;
      }
      const payload = await response.json();
      return this.normalizeVersion(payload?.version);
    } catch {
      return null;
    }
  }

  private handleVersionMismatch(
    latestKnownVersion: string,
    source: BuildVersionSource
  ): void {
    const mismatchSignature = `${this.currentBuildVersion}->${latestKnownVersion}`;
    const currentState = this.readReloadState();
    const nextCount =
      currentState && currentState.signature === mismatchSignature
        ? currentState.count + 1
        : 1;
    this.writeReloadState({
      signature: mismatchSignature,
      count: nextCount,
    });

    if (nextCount > this.hardReloadMaxAttempts) {
      this.forceLogoutAndHardRedirect("build-loop");
      return;
    }

    const attemptSignature = `${mismatchSignature}#${nextCount}`;
    if (
      sessionStorage.getItem(this.reloadGuardStorageKey) === attemptSignature ||
      this.refreshInProgress
    ) {
      return;
    }

    sessionStorage.setItem(this.reloadGuardStorageKey, attemptSignature);
    this.forceHardRefresh(source);
  }

  private resetReloadState(): void {
    localStorage.removeItem(this.reloadStateStorageKey);
    sessionStorage.removeItem(this.reloadGuardStorageKey);
  }

  private readReloadState(): ReloadState | null {
    const raw = localStorage.getItem(this.reloadStateStorageKey);
    if (!raw) {
      return null;
    }
    try {
      const parsed = JSON.parse(raw);
      const signature = String(parsed?.signature || "").trim();
      const count = Number(parsed?.count);
      if (!signature || !Number.isFinite(count) || count < 0) {
        return null;
      }
      return {
        signature,
        count: Math.trunc(count),
      };
    } catch {
      return null;
    }
  }

  private writeReloadState(state: ReloadState): void {
    localStorage.setItem(this.reloadStateStorageKey, JSON.stringify(state));
  }

  private promoteLatestVersion(version: string): void {
    const normalized = this.normalizeVersion(version);
    if (!normalized) {
      return;
    }
    localStorage.setItem(this.latestVersionStorageKey, normalized);
  }

  private compareVersions(currentVersion: string, otherVersion: string): number {
    const normalizedCurrent = this.normalizeVersion(currentVersion);
    const normalizedOther = this.normalizeVersion(otherVersion);
    if (normalizedCurrent === normalizedOther) {
      return 0;
    }

    const currentTs = this.extractReleaseTimestamp(normalizedCurrent);
    const otherTs = this.extractReleaseTimestamp(normalizedOther);
    if (currentTs !== null && otherTs !== null) {
      return currentTs > otherTs ? 1 : -1;
    }
    if (currentTs !== null) {
      return 1;
    }
    if (otherTs !== null) {
      return -1;
    }
    return normalizedCurrent > normalizedOther ? 1 : -1;
  }

  private extractReleaseTimestamp(version: string): number | null {
    const match = String(version || "").match(/^release:(\d+):/i);
    if (!match?.[1]) {
      return null;
    }
    const parsed = Number(match[1]);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private normalizeVersion(value: unknown): string {
    return String(value || "").trim();
  }

  private isAuthenticatedUserPresent(): boolean {
    return !!this.storageService.getAuthUser();
  }

  private resolveCurrentBuildVersion(): string {
    const runtimeBuildVersion =
      typeof window !== "undefined"
        ? this.normalizeVersion((window as any).__FORLAB_BUILD_VERSION__)
        : "";
    const buildReleaseToken = this.normalizeVersion(BUILD_RELEASE_TOKEN);
    const resolvedReleaseVersion = runtimeBuildVersion || buildReleaseToken;
    if (resolvedReleaseVersion) {
      return resolvedReleaseVersion;
    }

    const scripts = Array.from(this.document.querySelectorAll("script[src]"));
    const scriptSrc = scripts
      .map((node) => (node as HTMLScriptElement).src || "")
      .find((src) => /\/main(\.[^/?#]+)?\.js([?#].*)?$/i.test(src));

    if (!scriptSrc) {
      return environment.production ? "prod-main-unknown" : "dev-main-unknown";
    }

    const fileName = scriptSrc.split("/").pop()?.split("?")[0] || "";
    const hashMatch = fileName.match(/^main\.([^.]+)\.js$/i);
    if (hashMatch?.[1]) {
      return `main-${hashMatch[1]}`;
    }

    return environment.production ? "prod-main-nohash" : "dev-main-nohash";
  }

  private forceHardRefresh(source: BuildVersionSource): void {
    this.refreshInProgress = true;
    const { origin, pathname, hash, search } = window.location;
    const params = new URLSearchParams(search || "");
    params.set("__build_sync", `${Date.now()}`);
    params.set("__src", source);

    const nextSearch = params.toString();
    const url = `${origin}${pathname}${nextSearch ? `?${nextSearch}` : ""}${hash || ""}`;
    window.location.replace(url);
  }

  private forceLogoutAndHardRedirect(reason: string): void {
    this.refreshInProgress = true;
    this.storageService.clearAuth();
    localStorage.removeItem(this.authenticatedVersionStorageKey);
    localStorage.removeItem(this.latestVersionStorageKey);
    localStorage.removeItem(this.reloadStateStorageKey);
    sessionStorage.removeItem(this.reloadGuardStorageKey);

    const { origin, pathname, search } = window.location;
    const params = new URLSearchParams(search || "");
    params.set("__build_logout", `${Date.now()}`);
    params.set("__reason", reason);
    const nextSearch = params.toString();
    const url = `${origin}${pathname}${nextSearch ? `?${nextSearch}` : ""}#/authentication/signin`;
    window.location.replace(url);
  }
}
